import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Upload, User, X, Trash2 } from "lucide-react";

const roles = [
  { value: "administrator", label: "Administrator" },
  { value: "kepala_cabang", label: "Kepala Cabang" },
  { value: "finance", label: "Finance" },
  { value: "kasir", label: "Kasir" },
  { value: "capster", label: "Capster" },
  { value: "store_manager", label: "Store Manager" },
];

const roleBadgeColor = {
  administrator: "bg-[#E60000]/20 text-[#E60000]",
  kepala_cabang: "bg-orange-900/30 text-orange-400",
  finance: "bg-yellow-900/30 text-yellow-400",
  kasir: "bg-blue-900/30 text-blue-400",
  capster: "bg-green-900/30 text-green-400",
  store_manager: "bg-purple-900/30 text-purple-400",
};

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", role: "capster", photo_url: "" });
  const [uploading, setUploading] = useState(false);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await api.get("/employees");
      setEmployees(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const openAdd = () => { setEditing(null); setForm({ email: "", password: "", name: "", phone: "", role: "capster", photo_url: "" }); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ email: e.email, password: "", name: e.name, phone: e.phone || "", role: e.role, photo_url: e.photo_url || "" }); setDialogOpen(true); };

  const deleteEmployee = async (emp) => {
    if (!window.confirm(`Hapus karyawan "${emp.name}"?`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success("Karyawan dihapus");
      fetchEmployees();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Maks 4MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, photo_url: data.url }));
      toast.success("Foto diupload");
    } catch { toast.error("Upload gagal"); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, phone: form.phone, role: form.role, photo_url: form.photo_url };
        if (form.password) payload.password = form.password;
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Karyawan diperbarui");
      } else {
        await api.post("/employees", form);
        // If photo_url set, update it separately since create uses Pydantic model
        if (form.photo_url) {
          const emps = (await api.get("/employees")).data;
          const created = emps.find(emp => emp.email === form.email.toLowerCase().trim());
          if (created) await api.put(`/employees/${created.id}`, { photo_url: form.photo_url });
        }
        toast.success("Karyawan ditambahkan");
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="employees-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Karyawan</h1>
          <p className="text-gray-400 text-sm mt-1">Kelola karyawan barbershop</p>
        </div>
        <Button onClick={openAdd} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-10" data-testid="add-employee-btn">
          <Plus size={16} className="mr-2" /> Tambah
        </Button>
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="employees-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#262626]">
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Foto</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Nama</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Email</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Telepon</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Role</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Status</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A] transition-colors" data-testid={`employee-row-${emp.id}`}>
                <td className="px-5 py-3">
                  {emp.photo_url ? (
                    <img src={emp.photo_url} alt={emp.name} className="w-10 h-10 rounded-full object-cover border border-[#262626]" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[#262626] flex items-center justify-center">
                      <User size={16} className="text-gray-600" />
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-white font-semibold">{emp.name}</td>
                <td className="px-5 py-3 text-gray-400">{emp.email}</td>
                <td className="px-5 py-3 text-gray-400">{emp.phone || "-"}</td>
                <td className="px-5 py-3"><Badge className={roleBadgeColor[emp.role] || ""}>{emp.role}</Badge></td>
                <td className="px-5 py-3"><Badge className={emp.is_active ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}>{emp.is_active ? "Aktif" : "Nonaktif"}</Badge></td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(emp)} className="p-1.5 text-gray-400 hover:text-white hover:bg-[#262626] transition-colors" data-testid={`edit-employee-${emp.id}`}><Pencil size={14} /></button>
                    <button onClick={() => deleteEmployee(emp)} className="p-1.5 text-gray-400 hover:text-[#E60000] hover:bg-[#262626] transition-colors" data-testid={`delete-employee-${emp.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">{editing ? "Edit Karyawan" : "Tambah Karyawan"}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">Isi detail data karyawan</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="employee-form">
            {/* Photo Upload */}
            <div className="flex items-center gap-4">
              <div className="shrink-0">
                {form.photo_url ? (
                  <div className="relative">
                    <img src={form.photo_url} alt="Foto" className="w-20 h-20 rounded-full object-cover border-2 border-[#262626]" />
                    <button type="button" onClick={() => setForm({...form, photo_url: ""})} className="absolute -top-1 -right-1 w-5 h-5 bg-[#E60000] rounded-full flex items-center justify-center"><X size={10} className="text-white" /></button>
                  </div>
                ) : (
                  <label className="w-20 h-20 rounded-full bg-[#1A1A1A] border-2 border-dashed border-[#333] hover:border-[#E60000]/50 flex flex-col items-center justify-center cursor-pointer transition-colors" data-testid="employee-photo-upload">
                    <Upload size={16} className="text-gray-500 mb-1" />
                    <span className="text-[9px] text-gray-500">{uploading ? "..." : "Foto"}</span>
                    <input type="file" className="hidden" accept="image/*" onChange={e => handlePhotoUpload(e.target.files[0])} disabled={uploading} />
                  </label>
                )}
              </div>
              <div className="flex-1">
                <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Nama</Label>
                <Input data-testid="employee-name-input" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
              </div>
            </div>
            {!editing && (
              <div>
                <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Email</Label>
                <Input data-testid="employee-email-input" type="email" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
              </div>
            )}
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">{editing ? "Password Baru (kosongkan jika tidak diubah)" : "Password"}</Label>
              <Input data-testid="employee-password-input" type="password" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required={!editing} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Telepon</Label>
                <Input data-testid="employee-phone-input" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
              <div>
                <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Role</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="employee-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                    {roles.map(r => <SelectItem key={r.value} value={r.value} className="text-white">{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-10 rounded-none" data-testid="employee-submit-btn">
              {editing ? "Simpan Perubahan" : "Tambah Karyawan"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
